import Table from '@/components/Table'
import { TableData } from '@/components/Table/type'
import { useProductQuery } from '@/queries/useProductQuery'
import { Product } from '@/types/Product'
import { createColumnHelper } from '@tanstack/react-table'
import { useMemo } from 'react'
import DadosMestreHeader from '../components/DadosMestreHeader' 
import DadosMestreHeaderActions from '../components/DadosMestreHeaderActions'  
import DefaultColumn from '@/components/Table/components/DefaultColumn' 
import { useTable } from '@/hooks/useTable'
import { useProductSpreadsheets } from './useProductSpreadsheets'
import TableRowActions from '@/components/Table/components/TableRowActions'
import AddDialog from './components/AddDialog'
import DeleteDialog from './components/DeleteDialog'
import EditDialog from './components/EditDialog' 
import { Cog } from 'lucide-react'
import { TypographyH1, TypographyMuted } from '@/components/ui/typography'

export type ProductTable = TableData<Product>

const columnHelper = createColumnHelper<Product>()

const MasterDataProduct = () => {
    const {
        table,
        tableState,
        selectedRows, 
        isAddDialogOpen,
        isDeleteDialogOpen,
        selectedRow, 
        isEditDialogOpen,
        onEditDialogClose,
        onAddDialogClose,
        onClickMultiDelete, 
        setTable,
        onClickDelete,
        onClickEdit,
        onDeleteDialogClose,
        onClickAdd
    } = useTable<Product>('')

    const { onDownload } = useProductSpreadsheets() 

    const { data, isFetching, isPending, refetch } = useProductQuery()

    const memoData = useMemo(() => data || [], [data])
    const memoColumns = useMemo(
        () => [ 
            columnHelper.accessor('SK_PRODUTO', {
                id: 'SK_PRODUTO',
                header: 'Cod. Produto',
                size: 130,
            }), 
            columnHelper.accessor('DS_PRODUTO', {
                id: 'DS_PRODUTO',
                header: 'Produto',
                size: 440,
            }), 
            columnHelper.accessor('DD_SKU', {
                id: 'DD_SKU',
                header: 'Embalagem',
                size: 100,
            }), 
            columnHelper.accessor('DD_UNIDADE_VENDA', {
                id: 'DD_UNIDADE_MEDIDA',
                header: 'Uni. Venda',
                size: 120,
            }),  
            columnHelper.accessor('DD_MARCA', {
                id: 'DD_MARCA',
                header: 'Marca',
                size: 180,
            }),  
            columnHelper.accessor('DS_DETENTOR', {
                id: 'DS_DETENTOR',
                header: 'Fabricante',
                size: 120,
            }),  
            columnHelper.accessor('DD_ORIGEM_DADO', {
                id: 'DD_ORIGEM_DADO',
                header: 'Cadastro',
                size: 120,
            }),  
            columnHelper.display({
                id: 'Ações',
                cell: ({ row }) => { 
                    if (row.original.DD_ORIGEM_DADO == 'PROTHEUS') return 
                    return (
                        (
                            <TableRowActions
                                onClickDelete={() => onClickDelete(row)}
                                onClickEdit={() => onClickEdit(row)} 
                            />
                        )
                    )
                },
                size: 60,
                enableColumnFilter: false,
                enableSorting: false,
            }),
        ],
        []
    )

    return (
        <div className="flex flex-col h-full overflow-auto p-4">
             <div className="flex flex-col gap-4 justify-between mb-4 sm:items-center sm:flex-row">
                <div className="flex gap-3 items-center">
                    <div className="flex justify-center items-center w-10 h-10 rounded-lg bg-primary/10">
                        <Cog className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                        <TypographyH1>
                            Produtos
                        </TypographyH1>
                        <TypographyMuted>
                            Cadastre e efetue manutenções em produtos
                        </TypographyMuted>
                    </div>
                </div>
            </div>
            
            <div className="w-full h-full overflow-hidden">
                <Table<ProductTable>
                    data={memoData}
                    columns={memoColumns}
                    getRowId={(row) => row.SK_PRODUTO.toString()}
                    isLoading={isPending}
                    isFetching={isFetching}
                    getTableInstance={(table) => setTable(table)}   
                    tableState={tableState}  
                    tableHeader={
                        <DadosMestreHeader
                            title={``} 
                            onDelete={onClickMultiDelete}
                            selectedRows={
                                table
                                    ?.getFilteredRowModel()
                                    .flatRows.filter((row) => selectedRows[row.id])
                                    .length
                            }
                        />
                    }
                    tableActions={
                        <DadosMestreHeaderActions
                            onDownload={() => table && onDownload(table)}
                            onRefech={refetch}
                            onAdd={onClickAdd}
                        />
                    }  
                    defaultColumn={{
                        cell: ({ getValue }) => (
                            <DefaultColumn>{getValue() as string}</DefaultColumn>
                        ),
                    }}
                /> 

                {(isDeleteDialogOpen && selectedRow)  && (
                    <DeleteDialog
                        ids={selectedRow.original.SK_PRODUTO}
                        nameUser={selectedRow.original.DS_PRODUTO}
                        isOpen={isDeleteDialogOpen}
                        onClose={onDeleteDialogClose}
                    />
                )}
                {selectedRow && (
                    <EditDialog
                        row={selectedRow}
                        isOpen={isEditDialogOpen}
                        onClose={onEditDialogClose}
                    />
                )} 
                <AddDialog isOpen={isAddDialogOpen} onClose={onAddDialogClose} isFixedMark={false} />
            </div>
        </div>
    )
}

export default MasterDataProduct
